import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs.jsx';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useMessages } from '../../hooks/useMessages';
import { 
  MessageSquare, 
  Search, 
  Plus, 
  Mail, 
  MailOpen, 
  Star, 
  Archive, 
  Trash2,
  Filter,
  Clock,
  User,
  Paperclip
} from 'lucide-react';

const MessagesPage = () => {
  const [activeTab, setActiveTab] = React.useState('inbox');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedMessages, setSelectedMessages] = React.useState([]);
  const [filterUnread, setFilterUnread] = React.useState(false);
  
  const { messages, loading, error, fetchMessages } = useMessages({ 
    box: activeTab,
    search: searchTerm,
    unreadOnly: filterUnread
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedMessages([]);
  };

  const handleMessageSelect = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId) 
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const handleSelectAll = () => {
    const messagesArray = Array.isArray(messages) ? messages : [];
    if (selectedMessages.length === messagesArray.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messagesArray.map(m => m.id));
    }
  };

  const getMessageIcon = (message) => {
    if (message.isRead) return <MailOpen className="w-4 h-4 text-gray-400" />;
    return <Mail className="w-4 h-4 text-blue-500" />;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return messageDate.toLocaleDateString();
  };

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={() => fetchMessages()}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600 mt-1">Communicate with teachers, students, and parents</p>
        </div>
        <Button onClick={() => window.location.assign('/dashboard/messages/compose')}>
          <Plus className="w-4 h-4 mr-2" />
          Compose
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant={filterUnread ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterUnread(!filterUnread)}
              >
                <Mail className="w-4 h-4 mr-1" />
                Unread Only
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-1" />
                More Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="inbox" className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Inbox</span>
              {Array.isArray(messages) && messages.filter(m => !m.isRead).length > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {messages.filter(m => !m.isRead).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center space-x-2">
              <MailOpen className="w-4 h-4" />
              <span>Sent</span>
            </TabsTrigger>
            <TabsTrigger value="drafts" className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>Drafts</span>
            </TabsTrigger>
            <TabsTrigger value="starred" className="flex items-center space-x-2">
              <Star className="w-4 h-4" />
              <span>Starred</span>
            </TabsTrigger>
          </TabsList>

          {selectedMessages.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedMessages.length} selected
              </span>
              <Button variant="outline" size="sm">
                <Archive className="w-4 h-4 mr-1" />
                Archive
              </Button>
              <Button variant="outline" size="sm">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>

        <TabsContent value="inbox" className="space-y-4">
          {(!messages || messages.length === 0) ? (
            <EmptyState 
              icon={MessageSquare}
              title="No messages"
              description={searchTerm ? "No messages match your search criteria." : "Your inbox is empty."}
              action
              actionLabel="Refresh"
              onAction={() => fetchMessages()}
            />
          ) : (
            <div className="space-y-2">
              {/* Select All */}
              <div className="flex items-center space-x-2 p-2 border-b">
                <input
                  type="checkbox"
                  checked={Array.isArray(messages) && selectedMessages.length === messages.length && messages.length > 0}
                  onChange={handleSelectAll}
                  className="rounded"
                />
                <span className="text-sm text-gray-600">Select all</span>
              </div>

              {Array.isArray(messages) ? messages.map((message) => (
                <Card 
                  key={message.id} 
                  className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                    !message.isRead ? 'bg-blue-50 border-blue-200' : ''
                  } ${selectedMessages.includes(message.id) ? 'bg-blue-100' : ''}`}
                  onClick={() => window.location.assign(`/dashboard/messages/${message.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedMessages.includes(message.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleMessageSelect(message.id);
                        }}
                        className="mt-1 rounded"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            {getMessageIcon(message)}
                            <h3 className={`text-sm font-semibold truncate ${
                              !message.isRead ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {message.subject}
                            </h3>
                            {message.priority && (
                              <Badge className={getPriorityColor(message.priority)}>
                                {message.priority}
                              </Badge>
                            )}
                            {message.hasAttachments && (
                              <Paperclip className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              {formatTime(message.createdAt)}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle star/unstar
                              }}
                            >
                              <Star className={`w-4 h-4 ${
                                message.isStarred ? 'text-yellow-500 fill-current' : 'text-gray-400'
                              }`} />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {activeTab === 'inbox' ? `From: ${message.senderName}` : `To: ${message.recipientName}`}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {message.isImportant && (
                              <Badge variant="destructive" className="text-xs">Important</Badge>
                            )}
                            {message.isUrgent && (
                              <Badge variant="destructive" className="text-xs">Urgent</Badge>
                            )}
                          </div>
                        </div>
                        
                        {message.preview && (
                          <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                            {message.preview}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )) : null}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent">
          <div className="text-center py-12">
            <MailOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sent Messages</h3>
            <p className="text-gray-600">Your sent messages will appear here</p>
          </div>
        </TabsContent>

        <TabsContent value="drafts">
          <div className="text-center py-12">
            <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Draft Messages</h3>
            <p className="text-gray-600">Your draft messages will appear here</p>
          </div>
        </TabsContent>

        <TabsContent value="starred">
          <div className="text-center py-12">
            <Star className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Starred Messages</h3>
            <p className="text-gray-600">Your starred messages will appear here</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MessagesPage;


